import {Component, computed, DestroyRef, inject, Injector, OnInit, signal} from "@angular/core";
import {BasicMaterialDialog} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Translations} from "@translations";
import {ExamFlowRunMode, ExamFlowScheduleMode} from "@models";
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {ExamSchedulePageStore} from "../../../../web-manager/subModules/exam-schedule/store/exam-schedule-page.store";
import {effectOnceIf} from "ngxtension/effect-once-if";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {distinctUntilChanged, filter, skip} from "rxjs";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";

@Component({
	selector: "sim-exam-schedule-settings-dialog",
	templateUrl: "./exam-schedule-settings-dialog.component.html",
	styleUrl: "./exam-schedule-settings-dialog.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class ExamScheduleSettingsDialogComponent extends BasicMaterialDialog<ExamScheduleSettingsDialogComponent> implements OnInit {
	// Constants
	public readonly T = Translations;
	protected readonly ExamFlowScheduleMode = ExamFlowScheduleMode;
	protected readonly ExamFlowRunMode = ExamFlowRunMode;

	// Properties
	protected readonly form = new FormBuilder().nonNullable.group({
		scheduleMode: new FormControl<ExamFlowScheduleMode>(ExamFlowScheduleMode.AutoSchedule, Validators.required),
		runMode: new FormControl<ExamFlowRunMode>(ExamFlowRunMode.Default, Validators.required),
		nRunMaxRuns: new FormControl<number>({value: 0, disabled: true}, [Validators.required, Validators.min(1)]),
	});
	protected readonly hasChanged = signal<boolean>(false);
	protected readonly maxRunLimit = computed<number>(() =>
		Math.max(this.store.session()!.students.length, this.store.session()!.examStations.length),
	);

	// Injections
	readonly store = inject(ExamSchedulePageStore);
	readonly #destroyRef = inject(DestroyRef);

	// Effects
	readonly #effectObserveValueChanges = effectOnceIf(
		() => this.store.session(),
		() => {
			// Validate (doing this only for the linter, it should never occur)
			const session = this.store.session();
			if (!session) {
				throw new Error("session undefined in settings dialog");
			}

			this.form.controls.runMode.valueChanges
				.pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged())
				.subscribe((value) => {
					if (value === ExamFlowRunMode.NRun) {
						this.form.controls.nRunMaxRuns.enable({emitEvent: false});
					} else {
						this.form.controls.nRunMaxRuns.disable({emitEvent: false});
					}
				});

			this.form.valueChanges
				.pipe(
					takeUntilDestroyed(this.#destroyRef),
					filter(() => this.form.valid),
					skip(1),
				)
				.subscribe((value) => this.hasChanged.set(this.areSettingsDistinct(value, this.store.session()!.scheduleSettings)));

			this.form.setValue({
				scheduleMode: session.scheduleSettings.scheduleMode,
				runMode: session.scheduleSettings.runMode,
				nRunMaxRuns: Math.min(session.scheduleSettings.nRunMaxRuns, this.maxRunLimit()),
			});
		},
	);

	public constructor(_injector: Injector) {
		super(_injector);
	}

	protected async submit() {
		if (this.form.invalid) {
			return;
		}
		const value = this.form.value;
		await this.store.scheduleSettingsUpdated({
			nRunMaxRuns: value.nRunMaxRuns ?? 0,
			runMode: value.runMode!,
			scheduleMode: value.scheduleMode!,
		});
		this.close();
	}

	private areSettingsDistinct(
		valueA: Partial<{
			scheduleMode: ExamFlowScheduleMode | null;
			runMode: ExamFlowRunMode | null;
			nRunMaxRuns: number | null;
		}>,
		valueB: Partial<{
			scheduleMode: ExamFlowScheduleMode | null;
			runMode: ExamFlowRunMode | null;
			nRunMaxRuns: number | null;
		}>,
	): boolean {
		return !(
			(valueA.nRunMaxRuns ?? 0) === (valueB.nRunMaxRuns ?? 0) &&
			valueA.scheduleMode === valueB.scheduleMode &&
			valueA.runMode === valueB.runMode
		);
	}
}
